import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Empty, Layout, Menu } from "antd";
import { Link, useHistory } from "react-router-dom";
import ResizeObserver from "rc-resize-observer";

import Api from "api";
import { trackEvent } from "api/analytics";
import { getUserProfile } from "api";
import { setCollapsed, setContentVisited } from "reducers/layoutSlice";
import { useAskAccess } from "hooks/ask/useAgentCapabilities";

import { ReactComponent as ArrowSider } from "assets/Icon/Arrow/Arrow-Sider.svg";
import { ReactComponent as AskIcon } from "assets/IconsV2/Ask.svg";
import { ReactComponent as ArbTrackerIcon } from "assets/IconsV2/ArbTracker.svg";
import { ReactComponent as MapSearchIcon } from "assets/IconsV2/MapSearch.svg";
import { ReactComponent as TariffSearchIcon } from "assets/IconsV2/TariffSearch.svg";
import { ReactComponent as Form6SearchIcon } from "assets/IconsV2/Form6Search.svg";

import { ReactComponent as PrivacyIcon } from "assets/IconsV2/Privacy.svg";
import { ReactComponent as TermsIcon } from "assets/IconsV2/Terms.svg";

import { ReactComponent as GaugeIcon } from "assets/IconsV2/Throughputs.svg";
import { ReactComponent as OptmIcon } from "assets/IconsV2/Optm.svg";
import { ReactComponent as HeaderBell } from "assets/Icon/NotificationsBellSidebar.svg";
import SidebarLogo from "assets/logo/newLogo.svg";
import SmallLogo from "assets/logo/small-logo.svg";
import AccountMenu from "components/layout/AccountMenu";

const { Sider } = Layout;

export default function Sidebar({ option, isPublic, askOverlay = false, onNavigate }) {
    const history = useHistory();
    const dispatch = useDispatch();

    const sidebarCollapsed = useSelector(({ layout }) => layout.sidebarCollapsed);
    const taxonomyVisited = useSelector(({ layout }) => layout.taxonomyVisited);
    const [openKeys, setOpenKeys] = useState([]);
    const [selectedKeys, setSelectedKeys] = useState([option]);
    const [groups, setGroups] = useState([]);
    const [isShortScreen, setShortScreen] = useState();

    /*
     * Ask is gated differently from everything beside it. The other entries read Cognito groups
     * off the cached profile; Ask is a server capability, because a subscription can grant the
     * corpora it reads without any group saying so. So the sidebar asks the same endpoint the
     * page asks, and hides the entry rather than showing one whose only outcome is a refusal.
     */
    const { allowed: canAsk } = useAskAccess({ enabled: !isPublic });

    useEffect(() => {
        if (!isPublic) {
            const profile = getUserProfile();
            const { attributes, groups } = profile;
            dispatch(setContentVisited(attributes?.optm_visited));
            if (profile?.groups?.length) {
                setGroups(groups ? groups.map((d) => d.name) : []);
            } else {
                Api.patchUserAttributes().then(({ groups }) => {
                    setGroups(groups ? groups.map((d) => d.name) : []);
                });
            }
        }
    }, [dispatch, isPublic]);

    useEffect(() => {
        setOpenKeys([]);
        setSelectedKeys([option]);
    }, [option]);

    const onBreakPoint = (broken) => {
        dispatch(setCollapsed(broken));
    };

    return (
        <ResizeObserver
            onResize={({ width, height }) => {
                setShortScreen(height <= 580);
            }}
        >
            <Sider
                width={240}
                className={
                    "main-layout-sider" +
                    (isShortScreen ? " main-layout-sider--short" : "") +
                    (askOverlay ? " main-layout-sider--ask-overlay" : "")
                }
                breakpoint="xxl"
                /*
                 * The Ask overlay is a full drawer with labels, not the 80px icon rail. Collapsed
                 * here would hide every destination's name while the reader is choosing one.
                 */
                collapsed={askOverlay ? false : sidebarCollapsed || isPublic}
                onBreakpoint={askOverlay ? undefined : onBreakPoint}
            >
                {askOverlay ? null : (
                    <div
                        className="collapse-btn"
                        onClick={() => {
                            dispatch(setCollapsed(!sidebarCollapsed));
                        }}
                    >
                        <ArrowSider />
                    </div>
                )}
                <Empty
                    className="sider-logo"
                    image={sidebarCollapsed || isPublic ? SmallLogo : SidebarLogo}
                    description={false}
                />
                <Menu
                    mode="inline"
                    theme="dark"
                    className="main-menu"
                    selectedKeys={selectedKeys}
                    openKeys={openKeys}
                    forceSubMenuRender={true}
                    triggerSubMenuAction="click"
                >
                    {canAsk ? (
                        <Menu.Item
                            key="ask"
                            icon={<AskIcon />}
                            onClick={() => {
                                history.push("/ask");
                                trackEvent("Ask - Main Menu");
                                onNavigate?.();
                            }}
                        >
                            <Link
                                onClick={(e) => {
                                    e.preventDefault();
                                }}
                                to="/ask"
                            >
                                Ask
                            </Link>
                        </Menu.Item>
                    ) : null}
                    {groups.includes("ARB-TRACKER") || groups.includes("BASIC") ? (
                        <Menu.Item
                            key="tracker"
                            onClick={() => {
                                trackEvent("Arb Tracker - Main Menu");
                                history.push("/tracker");
                                onNavigate?.();
                            }}
                            icon={<ArbTrackerIcon />}
                        >
                            <Link
                                onClick={(e) => {
                                    e.preventDefault();
                                }}
                                to="/tracker"
                            >
                                Arb Tracker
                            </Link>
                        </Menu.Item>
                    ) : null}
                    {groups.includes("MAP-SEARCH") || groups.includes("BASIC") ? (
                        <Menu.Item
                            key="map"
                            icon={<MapSearchIcon />}
                            onClick={() => {
                                history.push("/map/search");
                                trackEvent("Map Search - Main Menu");
                                onNavigate?.();
                            }}
                        >
                            <Link
                                onClick={(e) => {
                                    e.preventDefault();
                                }}
                                to="/map/search"
                            >
                                Map Search
                            </Link>
                        </Menu.Item>
                    ) : null}

                    <Menu.Item
                        key="tariff"
                        icon={<TariffSearchIcon />}
                        onClick={() => {
                            history.push("/search");
                            trackEvent("Tariff Search - Main Menu");
                            onNavigate?.();
                        }}
                    >
                        <Link
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                            to="/search"
                        >
                            Tariff Search
                        </Link>
                    </Menu.Item>
                    {groups.includes("LEGACY-FORM6") || groups.includes("BASIC") ? (
                        <Menu.Item
                            key="form6"
                            icon={<Form6SearchIcon />}
                            onClick={() => {
                                history.push("/form6");
                                trackEvent("Form 6 - Main Menu");
                                onNavigate?.();
                            }}
                        >
                            <Link
                                onClick={(e) => {
                                    e.preventDefault();
                                }}
                                to="/form6"
                            >
                                Form 6 Search
                            </Link>
                        </Menu.Item>
                    ) : null}
                    {/*groups.includes("FLOW-ANALYTICS") || groups.includes("BASIC") ? (
                        <Menu.Item
                            key="models"
                            icon={<GaugeIcon />}
                            onClick={() => {
                                history.push("/models/throughput");
                                trackEvent("Throughput - Main Menu");
                            }}
                        >
                            <Link
                                to="/models/throughput"
                                onClick={(e) => {
                                    e.preventDefault();
                                }}
                            >
                                Throughput
                            </Link>
                        </Menu.Item>
                    ) : (
                        null
                    )*/}
                    <Menu.Item
                        key="optm"
                        className={!taxonomyVisited ? "menu-item-highlight" : ""}
                        icon={<OptmIcon />}
                        onClick={() => {
                            history.push("/optm");
                            trackEvent("OPTM - Main Menu");
                            onNavigate?.();
                        }}
                    >
                        <Link
                            to="/optm"
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                        >
                            OPTM
                        </Link>
                    </Menu.Item>
                    <Menu.Item
                        key="account"
                        icon={<HeaderBell style={{ padding: "2px" }} />}
                        onClick={() => {
                            history.push("/user/account");
                            trackEvent("Account Mgmt - Main Menu");
                            onNavigate?.();
                        }}
                    >
                        <Link
                            to="/user/account"
                            onClick={(e) => {
                                e.preventDefault();
                            }}
                        >
                            Favorites & Notifications
                        </Link>
                    </Menu.Item>
                </Menu>

                <Menu mode="inline" theme="dark" className="main-menu footer">
                    <Menu.Item
                        style={{ height: 68, paddingTop: 22 }}
                        icon={
                            <PrivacyIcon
                                style={{ marginLeft: -2 }}
                                onClick={() => {
                                    history.push("/privacy");
                                    onNavigate?.();
                                }}
                            />
                        }
                    >
                        <Link to="/privacy">Privacy</Link>
                    </Menu.Item>
                    <Menu.Item
                        style={{ height: 68, paddingTop: 22 }}
                        icon={
                            <TermsIcon
                                style={{ marginLeft: -2 }}
                                onClick={() => {
                                    history.push("/terms");
                                    onNavigate?.();
                                }}
                            />
                        }
                    >
                        <Link to="/terms">Terms</Link>
                    </Menu.Item>
                </Menu>
                {/*
                 * Account lives on the app sider rather than the page Header, so it stays put when
                 * Ask drops that Header and when the sider opens as Ask's phone overlay.
                 */}
                {!isPublic ? (
                    <div className="main-layout-sider__account">
                        <AccountMenu isPublic={isPublic} />
                    </div>
                ) : null}
            </Sider>
        </ResizeObserver>
    );
}
